import { Button, Card, Box, Container, IconButton, Table, TableBody, TableContainer, TableHead, TableRow, MenuItem, TableCell } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { enqueueSnackbar } from "notistack";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { EldLandmark, Organization } from "src/models";
import useModelData, { replaceById, deleteById } from "src/hooks/use-model-data";
import Iconify from "src/components/iconify/iconify";
import { DisplayPageData } from "src/components/display-page-data";
import useModelSavingData from "src/hooks/use-model-saving-data";

import SimpleDialog from "src/components/simple-dialog";
import PageHeader from "src/components/page-header";
import NoneFound from "src/components/none-found";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useRouter } from "src/routes/hooks";
import { useSearch } from "src/hooks/use-search";
import { SearchCard } from "src/components/search-card";
import DisplayErrors from "src/components/display-errors";
import { formatAddress } from "src/utils/address-utils";
import TruxSimpleSelectField from "src/components/inputs/trux-simple-select-field";
import NoneFoundReport from "src/components/none-found-report";
import TruxSimpleCheckboxField from "src/components/inputs/trux-simple-checkbox-field";

type EldLandmarkWithAssetCount = EldLandmark & {
  assetCount: number;
}

type EldAssetsPerLandmarkProps = {
  eldLandmarks?: EldLandmarkWithAssetCount[],
}

const EldAssetsPerLandmark = memo(({ eldLandmarks }: EldAssetsPerLandmarkProps) => {
  const { sortedList, sortData } = useSort(eldLandmarks);
  const router = useRouter();

  const {
    searchText,
    setSearchText,
    filteredList: searchedList,
  } = useSearch(sortedList, ["name"]);

  const [landmarkTypeFilter, setLandmarkTypeFilter] = useState("All");

  const filteredList = useMemo(() =>
    searchedList.filter(l => landmarkTypeFilter==="All" || landmarkTypeFilter === l.landmarkType),
    [landmarkTypeFilter,searchedList]
  );

  const [selectedLandmarkIds, setSelectedLandmarkIds] = useState<number[]>([]);

  if (!sortedList) {
    return null;
  }

  const totalAssets = filteredList.filter(a => selectedLandmarkIds.length === 0 || selectedLandmarkIds.includes(a.id)).reduce((a,b) => a + b.assetCount, 0);

  return <div>
    <SearchCard searchText={searchText} setSearchText={setSearchText} searchingFor="Landmarks">
      <Box>
        <TruxSimpleSelectField value={landmarkTypeFilter} onChange={(val) => setLandmarkTypeFilter(val)}>
          <MenuItem value="All" selected={landmarkTypeFilter === "All"}>All</MenuItem>
          <MenuItem value="Yard" selected={landmarkTypeFilter === "Yard"}>Yard</MenuItem>
          <MenuItem value="Terminal" selected={landmarkTypeFilter === "Terminal"}>Terminal</MenuItem>
          <MenuItem value="Pool" selected={landmarkTypeFilter === "Pool"}>Pool</MenuItem>
          <MenuItem value="Customer Site" selected={landmarkTypeFilter === "Customer Site"}>Customer Site</MenuItem>
        </TruxSimpleSelectField>
      </Box>
      <Box sx={{
        alignItems: "center",
        display: "flex",
        fontStyle: "italic",
        fontSize: "12px",
      }}>
        {totalAssets} assets {selectedLandmarkIds.length === 0 ? "found" : "selected"}
      </Box>
    </SearchCard>

    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TruxTableCell>
                <TruxSimpleCheckboxField size="medium" value={selectedLandmarkIds.length > 0} onChange={() => {
                  if (selectedLandmarkIds.length > 0) {
                    setSelectedLandmarkIds([]);
                  } else {
                    setSelectedLandmarkIds([...filteredList.map(l => l.id)]);
                  }
                }} />
              </TruxTableCell>
              <SortableHeader sortKey="name" sortData={sortData} >
                Landmark Name
              </SortableHeader>

              <TruxTableCell>
                Address
              </TruxTableCell>

              <TruxTableCell align="right">
                Asset Count
              </TruxTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredList.length === 0 && <TableRow>
              <TableCell rowSpan={3}>
                <NoneFoundReport padding={0} />
              </TableCell>
              </TableRow>}
            {filteredList.map((eldLandmark, index) =>
            (<TableRow
              key={index}
              hover
              onClick={() => router.push(`/app/eld/eld-assets-per-landmark/single/${eldLandmark.id}`)}
              sx={{ cursor: "pointer" }}
            >
              <TruxTableCell onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}>
                <TruxSimpleCheckboxField size="medium" value={selectedLandmarkIds.includes(eldLandmark.id)} onChange={() => {
                  if (selectedLandmarkIds.includes(eldLandmark.id)) {
                    const arr = [...selectedLandmarkIds];
                    arr.splice(arr.indexOf(eldLandmark.id), 1)
                    setSelectedLandmarkIds(arr);
                  } else {
                    setSelectedLandmarkIds([...selectedLandmarkIds, eldLandmark.id]);
                  }
                }} />
              </TruxTableCell>
              <TruxTableCell>
                {eldLandmark.name}
              </TruxTableCell>

              <TruxTableCell>
                {formatAddress(eldLandmark.mainAddress)}
              </TruxTableCell>

              <TruxTableCell align="right">
                {eldLandmark.assetCount}
              </TruxTableCell>
            </TableRow>
            ))}


            {filteredList.length > 0 && <TableRow>
              <TruxTableCell />
              <TruxTableCell>
                <b>Total {selectedLandmarkIds.length > 0 && "selected"}</b>
              </TruxTableCell>

              <TruxTableCell />

              <TruxTableCell align="right">
                <b>{totalAssets}</b>
              </TruxTableCell>
            </TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

type PageModel = {
  eldLandmarks?: EldLandmarkWithAssetCount[],
  organizations: Organization[],
}

export default function Page() {
  const settings = useSettingsContext();
  const dataLoader = useCallback(() => ({
    eldLandmarks: axios.get(endpoints.eldLandmarks.findAllWithAssetCount).then(response => response.data),
    organizations: axios.get(endpoints.eldOrganizations.childOrganizations).then(response => response.data),
  }), []);
  const {
    data,
    updateData,
    setData,
    error,
    loading,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);


  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {data && data?.eldLandmarks?.length !== 0 &&
          <PageHeader title="Assets per Landmark" />
        }

        {data && data?.eldLandmarks?.length === 0 &&
          <NoneFound title="No Landmarks Found" message="To add a Landmark click the 'Landmarks' button in the Sidebar." />
        }

        {data && data?.eldLandmarks?.length !== 0 && <EldAssetsPerLandmark eldLandmarks={data.eldLandmarks} />}
      </DisplayPageData>
    </Container>
  );
}
